<template>
  <TournamentCard
    v-if="raceData"
    :offer="snippet.format(raceData.prizeTitle)"
    :title="snippet.format(raceData.title)"
    :active-button-title="activeButtonTitle"
    :image-front="raceData.bannerCardFront"
    :image-back="raceData.bannerCardBack"
    :class="[
      {
        [$cardAction.actionFinaledRound]: raceData.actionFinaledRound,
      },
    ]"
    :is-finished-race="isTournamentsFinished && !isTournamentActive"
    :is-ended-timer="isTournamentsFinished"
    :timer-title-ended="durationDate"
    :ribbon-color="cardLabelColor"
    :timer-end-time="endTime || undefined"
    :timer-title="timerTitle"
    :ribbon-title="raceLabelTitle"
    active-button-type="primary-blue"
    @cardClick="openRaceOrSignupModal()"
    @buttonClick="openRaceOrSignupModal()"
  >
    <template #before-timer>
      <template v-if="isTournamentsFinished && checkTournamentStatus()">
        <ActionParticipate
          :is-not-formated="true"
          :is-action-participate-loser="true"
          :icon="storeComponents.infoDropdownAction.iconActionHeader.url"
          :text="storeComponents.infoDropdownAction.qualificationIsFinished"
        />
      </template>
      <template v-else>
        <ActionParticipate
          v-if="isJoined && isTournamentActive && !isUseInLoserGroup && !isUserInWinGroup"
          :is-not-formated="true"
          :icon="storeComponents.infoDropdownAction.iconActionHeader.url"
          :is-action-participate-successfull="true"
          :text="racesData.congratulationsMessageText"
        />
        <ActionParticipate
          v-if="isUseInLoserGroup && isTournamentActive && checkTournamentStatus()"
          :is-not-formated="true"
          :icon="storeComponents.infoDropdownAction.iconActionHeader.url"
          :text="storeComponents.infoDropdownAction.qualificationLosersIsSuccessfully"
        />
      </template>
    </template>
  </TournamentCard>
</template>

<script lang="ts" setup>
import ActionParticipate from '~/components/action/actionParticipateText.vue';
import { TournamentCard } from '~/components/lib/tournaments';
import { useSnippet } from '~/composables/useSnippet';
import { useStoreModals } from '~/stores/abstract/modals';
import { useStoreSsPlayer } from '~/stores/softswiss/player';
import { useStoreSsTournaments } from '~/stores/softswiss/tournaments';
import { useStoreButtonTitles } from '~/stores/strapi/buttonTitles';
import { useStoreComponents } from '~/stores/strapi/components';
import { EModalName } from '~/types/modals';
import { ERaceStatus, ERaceType } from '~/types/races';
import { formatDate } from '~/utils/date/helpers';
import type { ComponentInfoPageBlocksRacesBlock } from '~/types/infoPages';

const props = defineProps({
  raceSsId: {
    type: String,
    required: true,
  },
  data: {
    type: Object as PropType<ComponentInfoPageBlocksRacesBlock>,
    default: null,
  },
  isTournamentsFinished: {
    type: Boolean,
    default: false,
  },
});

const storeSsTournaments = useStoreSsTournaments();
const storeModals = useStoreModals();
const storeComponents = useStoreComponents();
const storeButtonTitles = useStoreButtonTitles();
const storeSsPlayer = useStoreSsPlayer();

const snippet = useSnippet();

const tournamentExtended = computed(() =>
  storeSsTournaments.tournamentsExtended.find(
    (t) => t.tournamentData.frontend_identifier?.toLowerCase() === props.raceSsId.toLowerCase()
  )
);

// Is the user in one of the groups a loser or not
const tournamentsListToShow = computed(() => {
  return storeSsTournaments.tournamentsExtended.filter(
    (tournament) => tournament.raceData.raceType === ERaceType.SpecialEvent
  );
});

const progressBars = computed(() => {
  return tournamentsListToShow.value.flatMap((tournament) => tournament.raceData.progressBar);
});

const isUserInWinGroup = computed(() => {
  return progressBars.value.some(
    (progressData) =>
      progressData.stageNumer !== 1 && storeSsPlayer.playerGroupIds.includes(progressData.groupId)
  );
});

const isUseInLoserGroup = computed(() => {
  return progressBars.value.some(
    (progressData) =>
      progressData.stageNumer !== 2 && storeSsPlayer.playerGroupIds.includes(progressData.groupId)
  );
});
// Is the user in one of the groups a loser or not

const status = computed(() => tournamentExtended.value?.tournamentData.status);

const isTournamentActive = computed(
  () => tournamentExtended.value?.tournamentData.status === ERaceStatus.active
);

const endTime = computed(() => {
  const timestamp =
    status.value === ERaceStatus.active
      ? tournamentExtended.value?.tournamentData.end_at
      : tournamentExtended.value?.tournamentData.start_at;

  return timestamp ? new Date(timestamp) : null;
});
const durationDate = computed(() =>
  tournamentExtended.value?.tournamentData.start_at &&
  tournamentExtended.value?.tournamentData.end_at
    ? `${formatDate(new Date(tournamentExtended.value?.tournamentData.start_at))} - ${formatDate(
        new Date(tournamentExtended.value?.tournamentData.end_at)
      )}`
    : ''
);

const raceData = computed(() => tournamentExtended.value?.raceData);
const activeButtonTitle = computed(() => storeButtonTitles.moreInfo);
const timerTitle = computed(() => storeSsTournaments.getRaceTimerTitle(status.value));
const cardLabelColor = computed(() =>
  status.value === ERaceStatus.scheduled
    ? 'grey'
    : storeSsTournaments.getRaceLabelColor(tournamentExtended.value?.raceData.raceLabel)
);
const raceLabelTitle = computed(() =>
  status.value === ERaceStatus.scheduled
    ? storeComponents.races.raceLabelComingSoonTitle
    : storeSsTournaments.getRaceLabelTitle(tournamentExtended.value?.raceData.raceLabel)
);
const racesData = computed(() => storeComponents.races);

const openRaceOrSignupModal = () => {
  if (storeSsPlayer.isPlayerAuthorised) {
    storeModals.openModal(EModalName.race, { [EModalName.race]: props.raceSsId });
  } else {
    storeModals.setReturnModal(EModalName.race, { [EModalName.race]: props.raceSsId });
    storeModals.openLoginOrSignupModal();
  }
};

const isJoined = computed(() => {
  return Boolean(
    storeSsTournaments.tournamentsStatuses.find(
      (p) => p.frontId === tournamentExtended.value?.raceData?.ssId
    )?.status
  );
});

const checkTournamentStatus = () => {
  return tournamentsListToShow.value.some((tournament) => {
    const matchingStatus = storeSsTournaments.tournamentsStatuses.find(
      (status) => status.frontId === tournament.raceData.ssId
    );
    return matchingStatus ? matchingStatus.status === true : false;
  });
};

onMounted(async () => {
  await storeSsTournaments.loadPlayerStatusesInTournaments();
});
</script>

<style lang="scss" module="$cardAction" src="./cardActions.scss"></style>
